import { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState,
  StatusBadge, type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Was die Projektleitung braucht — eine EIGENE Ansicht, nicht dieselbe wie
 * für die Planer.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE BLOCKADEN STEHEN OBEN, UND ZWAR AUS EINEM GRUND
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Bereich, der seit drei Wochen auf eine Architektenentscheidung wartet,
 * ist ein Terminrisiko — und das sieht man in keiner Aufgabenliste. Dort steht
 * die Aufgabe als „abgelehnt" und ist damit vom Tisch, während in Wirklichkeit
 * niemand weiterarbeiten kann.
 *
 * Deshalb steht sie als erste Tabelle und nicht als Spalte in einer anderen.
 */
export function Projektleitung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(() => {
    if (!projekt) return;
    setFehler(null);
    api.koordinationProjektleitung(projekt.id).then(setD).catch(setFehler);
  }, [api, projekt]);

  useEffect(() => { laden(); }, [laden]);

  const kopf = (
    <PageHeader
      titel="Projektleitung — Koordination"
      beschreibung={'Blockaden und Trends statt der nächsten Aufgabe: Was hält auf, '
        + 'wie weit ist jeder Abschnitt, und welche Regel bildet die Wirklichkeit '
        + 'nicht ab.'}
      aktionen={projekt && (
        <>
          <Link to={`/projekte/${projekt.id}/koordination/nachtlauf`} className="m-knopf" style={{ textDecoration: 'none' }}>
            Nächtlicher Lauf
          </Link>
          <Link to={`/projekte/${projekt.id}/koordination`} className="m-knopf" style={{ textDecoration: 'none' }}>
            Koordination
          </Link>
        </>
      )}
    />
  );

  if (fehler && !d) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Auswertung für die Projektleitung konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!projekt || !d) {
    return (
      <>
        {kopf}
        <LoadingState
          text={projekt ? 'Auswertung wird gerechnet …' : 'Projekt wird geladen …'}
          zeilen={6}
          spalten={5}
        />
      </>
    );
  }

  const z = (x: number) => (x ?? 0).toLocaleString('de-DE');

  const blockaden = (d.blockaden ?? []) as any[];
  const bereiche = (d.bereiche ?? []) as any[];
  const gewerke = (d.gewerke ?? []) as any[];
  const regeln = (d.regeln ?? []) as any[];

  const blockadenSpalten: Spalte<any>[] = [
    {
      schluessel: 'alter', titel: 'Alter', art: 'zahl', einheit: 'Tage',
      breite: '110px',
      wert: (b) => b.aeltesteTage,
      // Über zwei Wochen ist keine Wartezeit mehr, sondern ein Termin­risiko.
      inhalt: (b) => (b.aeltesteTage > 14
        ? <StatusBadge zustand="fehler" text={`${b.aeltesteTage} Tage`} klein />
        : <span>{b.aeltesteTage > 0 ? `${b.aeltesteTage} Tage` : 'heute'}</span>),
    },
    {
      schluessel: 'bereich', titel: 'Abschnitt', immerSichtbar: true,
      wert: (b) => b.bereich ?? '',
      inhalt: (b) => b.bereich ?? '—',
    },
    {
      schluessel: 'gewerk', titel: 'Gewerk',
      wert: (b) => b.gewerk ?? '',
      inhalt: (b) => b.gewerk ?? '—',
    },
    {
      schluessel: 'grund', titel: 'Grund',
      wert: (b) => b.grund,
      inhalt: (b) => (
        <>
          {b.grund}
          {b.istEskalation && (
            <> <StatusBadge zustand="warnung" text="Eskalation" klein /></>
          )}
        </>
      ),
    },
    {
      schluessel: 'anzahl', titel: 'Aufgaben', art: 'zahl', breite: '110px',
      wert: (b) => b.anzahl,
    },
  ];

  const bereichSpalten: Spalte<any>[] = [
    {
      schluessel: 'abschnitt', titel: 'Abschnitt', immerSichtbar: true,
      wert: (b) => b.kennzeichen,
      inhalt: (b) => (
        <>
          {b.kennzeichen}
          {b.bezeichnung && (
            <div className="schwach" style={{ fontSize: 11 }}>{b.bezeichnung}</div>
          )}
        </>
      ),
    },
    { schluessel: 'zustand', titel: 'Zustand', wert: (b) => b.zustand },
    { schluessel: 'offen', titel: 'offen', art: 'zahl', wert: (b) => b.offen },
    {
      schluessel: 'entschieden', titel: 'entschieden', art: 'zahl',
      wert: (b) => b.entschieden,
    },
    {
      schluessel: 'stand', titel: 'Stand', art: 'zahl', einheit: '%',
      // Ein Stand über null Aufgaben ist nicht 100 %, sondern nicht definiert.
      wert: (b) => b.bearbeitungsstandProzent,
    },
    {
      schluessel: 'regression', titel: 'Regression', art: 'zahl',
      wert: (b) => b.regressionen,
      inhalt: (b) => (b.regressionen > 0
        ? <StatusBadge zustand="fehler" text={z(b.regressionen)} klein />
        : '0'),
    },
    {
      schluessel: 'blockiert', titel: 'blockiert', art: 'zahl',
      wert: (b) => b.blockaden,
      inhalt: (b) => (b.blockaden > 0
        ? <StatusBadge zustand="fehler" text={z(b.blockaden)} klein />
        : '0'),
    },
    {
      schluessel: 'woche', titel: 'Woche', art: 'zahl',
      wert: (b) => b.durchsatzWoche,
    },
    {
      schluessel: 'befund', titel: 'Befund',
      wert: (b) => b.befund,
      inhalt: (b) => <span style={{ fontSize: 12 }}>{b.befund}</span>,
    },
  ];

  const gewerkSpalten: Spalte<any>[] = [
    { schluessel: 'gewerk', titel: 'Gewerk', immerSichtbar: true, wert: (g) => g.gewerk },
    { schluessel: 'offen', titel: 'offen', art: 'zahl', wert: (g) => g.offen },
    {
      schluessel: 'entschieden', titel: 'entschieden', art: 'zahl',
      wert: (g) => g.entschieden,
    },
    {
      schluessel: 'stand', titel: 'Stand', art: 'zahl', einheit: '%',
      wert: (g) => g.bearbeitungsstandProzent,
    },
    { schluessel: 'woche', titel: 'Woche', art: 'zahl', wert: (g) => g.durchsatzWoche },
    {
      schluessel: 'vorwoche', titel: 'Vorwoche', art: 'zahl',
      wert: (g) => g.durchsatzVorwoche,
    },
    {
      schluessel: 'trend', titel: 'Trend',
      wert: (g) => g.trend,
      inhalt: (g) => <span style={{ fontSize: 12 }}>{g.trend}</span>,
    },
  ];

  const regelSpalten: Spalte<any>[] = [
    {
      schluessel: 'gewerk', titel: 'Gewerk', immerSichtbar: true,
      wert: (r) => r.gewerk ?? '',
      inhalt: (r) => r.gewerk ?? '—',
    },
    {
      schluessel: 'gegen', titel: 'gegen',
      wert: (r) => r.gegenGewerk ?? '',
      inhalt: (r) => r.gegenGewerk ?? '—',
    },
    { schluessel: 'richtung', titel: 'Richtung', wert: (r) => r.richtung },
    {
      schluessel: 'entschieden', titel: 'entschieden', art: 'zahl',
      wert: (r) => r.entschieden,
    },
    {
      schluessel: 'abgelehnt', titel: 'abgelehnt', art: 'zahl', einheit: '%',
      wert: (r) => r.ablehnquote * 100,
      inhalt: (r) => (
        <StatusBadge
          zustand={r.ablehnquote > 0.5 ? 'fehler' : 'warnung'}
          text={`${(r.ablehnquote * 100).toFixed(0)} %`}
          klein
        />
      ),
    },
    {
      schluessel: 'hauptgrund', titel: 'häufigster Grund',
      wert: (r) => r.hauptgrund,
      inhalt: (r) => <span style={{ fontSize: 12 }}>{r.hauptgrund}</span>,
    },
  ];

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="Offen" wert={d.offenGesamt} />
        <KpiCard titel="Entschieden" wert={d.entschiedenGesamt} />
        <KpiCard
          titel="Blockiert" wert={d.blockadenGesamt}
          zustand={d.blockadenGesamt > 0 ? 'fehler' : undefined}
          zustandText={d.blockadenGesamt > 0 ? 'wartet auf eine fremde Entscheidung' : undefined}
        />
        <KpiCard
          titel="Diese Woche erledigt" wert={d.durchsatzWoche}
          vergleich="laufende Kalenderwoche"
        />
      </KpiReihe>

      {/* ══════════════════════════════════════════════════════════════════
          DIE BLOCKADEN — ZUERST
          ══════════════════════════════════════════════════════════════════ */}
      <DataTable
        name="Blockaden"
        beschreibung={'Bereiche, die auf eine fremde Entscheidung warten. In der '
          + 'Aufgabenliste stehen sie als „abgelehnt" und sind damit vom Tisch — in '
          + 'Wirklichkeit kann dort niemand weiterarbeiten. Das ist ein Terminrisiko '
          + 'und keine Planungsarbeit.'}
        spalten={blockadenSpalten}
        zeilen={blockaden}
        schluessel={(b) => `${b.bereich ?? ''}|${b.gewerk ?? ''}|${b.grund}`}
        sortierung={{ spalte: 'alter', richtung: 'ab' }}
        ausfuhr
        leer={(
          <EmptyState
            titel="Keine Blockaden."
            erklaerung={'Es wartet kein Bereich auf eine fremde Entscheidung — was auch '
              + 'heißen kann, dass noch niemand eskaliert hat.'}
          />
        )}
      />

      <DataTable
        name="Je Abschnitt"
        spalten={bereichSpalten}
        zeilen={bereiche}
        schluessel={(b) => b.kennzeichen}
        ausfuhr
        leer={(
          <EmptyState
            titel="Noch kein Abschnitt ausgewertet."
            erklaerung={'Abschnitte entstehen aus den Planungsabschnitten der '
              + 'Koordinationsgrundlagen.'}
          />
        )}
      />

      {gewerke.length > 0 && (
        <DataTable
          name="Je Gewerk"
          spalten={gewerkSpalten}
          zeilen={gewerke}
          schluessel={(g) => g.gewerk}
          ausfuhr
        />
      )}

      {regeln.length > 0 && (
        <DataTable
          name="Regeln in die Regelpflege"
          beschreibung={'Eine Ablehnungsquote über der Hälfte heißt: Die Regel ist '
            + 'falsch, nicht der Planer.'}
          spalten={regelSpalten}
          zeilen={regeln}
          schluessel={(r) => `${r.gewerk ?? ''}|${r.gegenGewerk ?? ''}|${r.richtung}`}
          sortierung={{ spalte: 'abgelehnt', richtung: 'ab' }}
          ausfuhr
        />
      )}

      {((d.hinweise ?? []) as string[]).map((h, i) => (
        <div key={i} className="hinweis">{h}</div>
      ))}
    </>
  );
}
